/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import { useQuery } from '@tanstack/react-query';

import { qualifyUrl } from 'util/URLUtils';
import UserNotification from 'util/UserNotification';
import fetch from 'logic/rest/FetchProvider';
import { defaultOnError } from 'util/conditional/onError';

export type AthenaConnectorCredentials = {
  aws_region?: string;
  aws_access_key?: string;
  aws_iam_role?: string;
  id?: string;
  stream_id?: string;
  data_lake_name?: string;
  s3_output_bucket?: string;
  aws_secret_key?: {
    is_set: boolean;
  };
};

export type SaveAthenaConnectorCredentials = {
  aws_region?: string;
  aws_access_key?: string;
  aws_iam_role?: string;
  id?: string;
  stream_id?: string;
  stream_title?: string;
  stream_description?: string;
  data_lake_name?: string;
  s3_output_bucket?: string;
  aws_secret_key?: {
    set_value: string;
  };
};

export const saveDataLakeConnector = async (connector: SaveAthenaConnectorCredentials): Promise<string | void> => {
  try {
    const { id } = await fetch(
      'POST',
      qualifyUrl(
        `/plugins/org.graylog.plugins.datalake.externaldatalake.datasources.athena/athena/credentials/save`,
      ),
      connector,
    );

    UserNotification.success(`Data Lake Connector saved successfully.`);

    return id;
  } catch (errorThrown) {
    UserNotification.error(
      `Saving Data Lake Connector failed with status: ${errorThrown}`,
      'Could not save the Data Lake Connector.',
    );
  }

  return null;
};

export const deleteDataLakeConnector = async (id: string): Promise<void> => {
  try {
    await fetch(
      'DELETE',
      qualifyUrl(
        `/plugins/org.graylog.plugins.datalake.externaldatalake.datasources.athena/athena/credentials/${id}`,
      ),
    );

    UserNotification.success(`Data Lake Connector deleted successfully.`);
  } catch (errorThrown) {
    UserNotification.error(
      `Deleting Data Lake Connector failed with status: ${errorThrown}`,
      'Could not delete the Data Lake Connector.',
    );
  }
};

export const fetchDataLakeConnectors = async (): Promise<AthenaConnectorCredentials[]> =>
  fetch(
    'GET',
    qualifyUrl(`/plugins/org.graylog.plugins.datalake.externaldatalake.datasources.athena/athena/credentials`),
  );

export const DataLakeConnectorsQueryKey = 'data-lake-connectors';

const useDataLakeConnectors = (refetchInterval: number | false = 5000) => {
  const { data, refetch, isLoading, error } = useQuery({
    queryKey: [DataLakeConnectorsQueryKey],
    queryFn: () =>
      defaultOnError(
        fetchDataLakeConnectors(),
        'Loading Data Lake Connectors failed with status',
        'Could not load Data Lake Connectors.',
      ),
    notifyOnChangeProps: ['data', 'error'],
    refetchInterval,
  });

  return {
    data,
    refetch,
    isLoading,
    error,
  };
};

export default useDataLakeConnectors;
